import React, { useEffect, useState } from 'react';
import { getBackgroundJobs, runAccessExpiryJob, getTokenHealth, exportAuditToSiem } from '../services/api';
import './AdminPages.css';

const JobsDashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [health, setHealth] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const [j, h] = await Promise.all([getBackgroundJobs(), getTokenHealth()]);
      setJobs(j.data || []);
      setHealth(h.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load');
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="admin-page">
      <h1 className="page-title">Jobs & GitHub API health</h1>
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <div className="card">
        <div className="page-header-row">
          <h3>Background jobs</h3>
          <button className="btn btn-primary btn-sm" onClick={async () => {
            try {
              await runAccessExpiryJob();
              setMessage('Access expiry job completed');
              load();
            } catch (err) {
              setError(err.response?.data?.error || 'Job failed');
            }
          }}>Run access expiry now</button>
          <button className="btn btn-secondary btn-sm" onClick={async () => {
            try {
              const res = await exportAuditToSiem(24);
              setMessage(res.data.message || 'SIEM export finished');
              load();
            } catch (err) {
              setError(err.response?.data?.error || 'SIEM export failed');
            }
          }}>Export audit to SIEM</button>
        </div>
        <table className="simple-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Last run</th>
              <th>Duration</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job._id || job.name}>
                <td>{job.name}</td>
                <td><span className={`badge status-${job.lastStatus}`}>{job.lastStatus}</span></td>
                <td>{job.lastRunAt ? new Date(job.lastRunAt).toLocaleString() : 'never'}</td>
                <td>{job.lastDurationMs ? `${job.lastDurationMs}ms` : '—'}</td>
                <td>{job.lastMessage || '—'}</td>
              </tr>
            ))}
            {jobs.length === 0 && (
              <tr><td colSpan={5}>No jobs recorded yet (they appear after first run).</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>GitHub token health & rate limit</h3>
        {health && (
          <>
            <div className="stat-grid">
              <div className="stat-card">
                <div className="stat-label">Tokens checked</div>
                <div className="stat-value">{health.totalTokens}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">OK / Invalid</div>
                <div className="stat-value">{health.ok} / {health.failed}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Lowest rate-limit remaining</div>
                <div className="stat-value">{health.lowestRateLimitRemaining ?? '—'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">GitHub App</div>
                <div className="stat-value">{health.githubAppConfigured ? 'Configured' : 'Off'}</div>
              </div>
            </div>
            <table className="simple-table">
              <thead>
                <tr><th>User</th><th>Role</th><th>Status</th><th>Rate limit</th><th>Error</th></tr>
              </thead>
              <tbody>
                {(health.tokens || []).map((t) => (
                  <tr key={t.userId}>
                    <td>{t.username} {t.githubUsername ? `(@${t.githubUsername})` : ''}</td>
                    <td>{t.role}</td>
                    <td>{t.status}</td>
                    <td>{t.rateLimitRemaining ?? '—'}</td>
                    <td>{t.error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
      </div>
    </div>
  );
};

export default JobsDashboard;
